import { fetchJsonWithCsrf } from "./common"

const STALL_TIMEOUT_SECONDS = 30
const STALL_CHECK_INTERVAL_MS = 5000

// Drives a presigned direct-to-S3 upload: create record, PUT bytes with progress, complete.
export default class PresignedUpload {
  constructor(createUrl, { onProgress } = {}) {
    this.createUrl = createUrl
    this.onProgress = onProgress || (() => {})
    this.xhr = null
    this.destroyUrl = null
    this.stallCheckInterval = null
    this.lastProgressAt = null
    this.uploadStalled = false
  }

  async start(file, contentType = file.type) {
    const fileUpload = await this.createFileUpload(file, contentType)
    this.destroyUrl = fileUpload.destroy_url

    await this.uploadFileToS3(file, fileUpload)

    return this.completeFileUpload(fileUpload)
  }

  isUploading() {
    return !!this.xhr
  }

  cancel() {
    if (!this.xhr)
      return

    this.stopStallCheck()
    this.xhr.abort()
    this.xhr = null
  }

  createFileUpload(file, contentType) {
    return fetchJsonWithCsrf(this.createUrl, "POST", {
      file_upload: {
        filename: file.name,
        content_type: contentType,
        byte_size: file.size
      }
    })
  }

  uploadFileToS3(file, fileUpload) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      this.xhr = xhr

      xhr.open(fileUpload.upload_method, fileUpload.upload_url, true)
      this.applyUploadHeaders(xhr, fileUpload.upload_headers)

      xhr.upload.addEventListener("progress", (event) => {
        this.lastProgressAt = Date.now()
        this.reportProgress(event)
      })

      xhr.addEventListener("load", () => {
        this.stopStallCheck()

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
          return
        }

        reject(new Error("Upload to S3 failed"))
      })

      xhr.addEventListener("error", () => {
        this.stopStallCheck()
        reject(new Error("Upload to S3 failed"))
      })

      xhr.addEventListener("abort", () => {
        this.stopStallCheck()

        if (this.uploadStalled) {
          this.uploadStalled = false
          reject(new Error("Upload stalled — no progress for 30 seconds. Please check your connection and try again."))
          return
        }

        reject(new DOMException("Upload aborted", "AbortError"))
      })

      this.startStallCheck()
      xhr.send(file)
    })
  }

  reportProgress(event) {
    if (!event.lengthComputable)
      return

    this.onProgress(Math.round((event.loaded / event.total) * 100))
  }

  applyUploadHeaders(xhr, uploadHeaders) {
    Object.entries(uploadHeaders || {}).forEach(([ key, value ]) => {
      xhr.setRequestHeader(key, value)
    })
  }

  async completeFileUpload(fileUpload) {
    if (!fileUpload.complete_url)
      throw new Error("Missing complete URL")

    const completed = await fetchJsonWithCsrf(fileUpload.complete_url, "POST")

    if (!completed.uploadable_id)
      throw new Error("Missing uploadable id")

    return completed
  }

  async destroyRecord({ silent = false } = {}) {
    if (!this.destroyUrl)
      return

    const destroyUrl = this.destroyUrl
    this.destroyUrl = null

    try {
      await fetchJsonWithCsrf(destroyUrl, "DELETE")
    }
    catch (error) {
      if (!silent)
        throw error
    }
  }

  startStallCheck() {
    this.lastProgressAt = Date.now()
    this.uploadStalled = false

    this.stallCheckInterval = setInterval(() => {
      if (!this.isUploadStalled())
        return

      this.uploadStalled = true
      this.stopStallCheck()
      this.xhr?.abort()
    }, STALL_CHECK_INTERVAL_MS)
  }

  stopStallCheck() {
    if (!this.stallCheckInterval)
      return

    clearInterval(this.stallCheckInterval)
    this.stallCheckInterval = null
    this.lastProgressAt = null
  }

  isUploadStalled() {
    if (!this.lastProgressAt)
      return false

    return (Date.now() - this.lastProgressAt) / 1000 > STALL_TIMEOUT_SECONDS
  }
}
